import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, MapPin, Users, CalendarCheck, Clock, UserX, AlertTriangle, Wrench } from "lucide-react";
import { PageLoading, StatCard } from "@/components/shared";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

export default function CareDispatch() {
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const [ov, setOv] = useState(null);
  const nav = useNavigate();
  useEffect(() => { setD(null); api.get(`/care/dispatch?date=${date}`).then((r) => setD(r.data)).catch(() => setD({})); api.get(`/care/time/overview?date=${date}`).then((r) => setOv(r.data)).catch(() => setOv(null)); }, [date]);

  return (
    <div data-testid="care-dispatch-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><LayoutDashboard className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Disposition · Heute</h1></div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="dispatch-date" />
      </div>
      {!d ? <PageLoading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Geplante Touren" value={d.planned_tours ?? 0} accent="cyan" icon={MapPin} testid="dm-tours" />
            <StatCard label="Aktive Mitarbeiter" value={d.active_workers ?? 0} accent="navy" icon={Users} testid="dm-workers" />
            <StatCard label="Geplante Besuche" value={d.planned_visits ?? 0} accent="navy" icon={CalendarCheck} testid="dm-visits" />
            <StatCard label="Verspätete Besuche" value={d.late_visits ?? 0} accent="amber" icon={Clock} testid="dm-late" />
            <StatCard label="Nicht zugewiesen" value={d.unassigned_visits ?? 0} accent={d.unassigned_visits ? "red" : "slate"} icon={UserX} testid="dm-unassigned" />
            <StatCard label="Konflikte" value={d.conflicts ?? 0} accent={d.conflicts ? "red" : "slate"} icon={AlertTriangle} testid="dm-conflicts" />
            <StatCard label="Planungsfehler" value={d.planning_errors ?? 0} accent={d.planning_errors ? "amber" : "slate"} icon={Wrench} testid="dm-errors" />
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {ov && <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="dm-live">
              <div className="mb-3 flex flex-wrap gap-4 text-sm">
                <span className="font-semibold text-navy">Heute-Live:</span>
                <span data-testid="dm-live-shift">Im Dienst <b>{ov.workers_on_shift}</b></span>
                <span data-testid="dm-live-patient">Beim Patienten <b>{ov.at_patient}</b></span>
                <span data-testid="dm-live-underway">Unterwegs <b>{ov.underway}</b></span>
              </div>
              <div className="divide-y divide-slate-100">
                {(ov.workers || []).map((w) => (
                  <div key={w.worker_id} className="flex items-center justify-between py-2 text-sm" data-testid={`dm-live-w-${w.worker_id}`}>
                    <span className="font-semibold text-navy">{w.name || w.worker_id}</span>
                    <span className="text-slate-500">{w.status === "AT_PATIENT" ? `Beim Patienten ${w.patient_number || ""}` : w.status === "UNDERWAY" ? "Unterwegs" : "Im Dienst"}</span>
                  </div>
                ))}
                {(!ov.workers || ov.workers.length === 0) && <div className="py-2 text-sm text-slate-400">Aktuell niemand im Dienst.</div>}
              </div>
            </div>}
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dm-unassigned-list">
              <h3 className="mb-3 font-heading text-lg font-extrabold text-navy">Nicht zugewiesene Besuche</h3>
              <div className="divide-y divide-slate-100">
                {(d.unassigned || []).map((v) => (
                  <button key={v.id} onClick={() => nav(`/app/employer/pflege/visits/${v.id}`)} className="flex w-full items-center justify-between py-2.5 text-left hover:bg-slate-50" data-testid={`dm-un-${v.id}`}>
                    <div><div className="text-sm font-semibold text-navy">{v.patient_name}</div><div className="font-mono text-xs text-slate-400">{v.visit_number} · {v.scheduled_start || "—"}</div></div>
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">Zuweisen</span>
                  </button>
                ))}
                {(!d.unassigned || d.unassigned.length === 0) && <div className="py-3 text-sm text-slate-400">Alle Besuche zugewiesen.</div>}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dm-conflict-list">
              <h3 className="mb-3 font-heading text-lg font-extrabold text-navy">Terminüberschneidungen</h3>
              <div className="space-y-2">
                {(d.conflict_list || []).map((c, i) => (
                  <div key={i} className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid={`dm-conf-${i}`}>{c.message}</div>
                ))}
                {(!d.conflict_list || d.conflict_list.length === 0) && <div className="py-3 text-sm text-slate-400">Keine Konflikte.</div>}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => nav("/app/employer/pflege/tours")} className="rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90" data-testid="dm-goto-tours">Touren planen</button>
            <button onClick={() => nav("/app/employer/pflege/visits")} className="rounded-full bg-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-dark" data-testid="dm-goto-visits">Besuche ansehen</button>
          </div>
        </>
      )}
    </div>
  );
}
